import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Trash2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { useAuthStore } from '@/stores/auth';
import {
    useAddContactLogEntry,
    useEditContactLogEntry,
    useDeleteContactLogEntry,
    CONTACT_LOG_TYPES,
    type ClientContactLogEntry,
    type ContactLogType,
} from '@/api/clients';
import { SelectField } from './SelectField';

/*
 * Shared dialog for adding or editing a contact-log entry. When `entry`
 * is non-null the form opens pre-populated in edit mode and exposes a
 * Delete button. When null it opens blank for a new entry.
 *
 * The server is the authoritative validator — same shape rules (date
 * format, recognized type, non-empty note) are enforced there too and
 * the dialog surfaces any response error through an Alert. Notes in
 * the contact log frequently contain PHI, so every save / delete
 * writes an audit row server-side.
 */

interface ContactLogDialogProps {
    clientId: string;
    entry: ClientContactLogEntry | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type FormState = {
    date: string;
    type: ContactLogType;
    by: string;
    note: string;
};

const DEFAULT_TYPE: ContactLogType = 'Phone Call';

function todayISO(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function ContactLogDialog({
    clientId, entry, open, onOpenChange,
}: ContactLogDialogProps) {
    const me = useAuthStore((s) => s.user);
    const addMut = useAddContactLogEntry(clientId);
    const editMut = useEditContactLogEntry(clientId);
    const deleteMut = useDeleteContactLogEntry(clientId);

    const isEdit = entry !== null;
    const pending = addMut.isPending || editMut.isPending || deleteMut.isPending;

    const [form, setForm] = useState<FormState>(() => initialForm(entry, me));
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(initialForm(entry, me));
            setFieldError(null);
            setConfirmingDelete(false);
            addMut.reset();
            editMut.reset();
            deleteMut.reset();
        }
    }, [open, entry]); // eslint-disable-line react-hooks/exhaustive-deps

    function update<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        const note = form.note.trim();
        if (!note) {
            setFieldError('Note is required.');
            return;
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
            setFieldError('Enter a valid date.');
            return;
        }
        setFieldError(null);

        try {
            if (isEdit && entry) {
                await editMut.mutateAsync({
                    entryId: entry.id,
                    payload: { date: form.date, type: form.type, note, by: form.by.trim() },
                });
            } else {
                await addMut.mutateAsync({
                    date: form.date, type: form.type, note, by: form.by.trim() || undefined,
                });
            }
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    async function onDelete() {
        if (!entry) return;
        if (!confirmingDelete) {
            setConfirmingDelete(true);
            return;
        }
        try {
            await deleteMut.mutateAsync(entry.id);
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = addMut.error || editMut.error || deleteMut.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to save entry';
        return 'Unexpected error';
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Contact Log Entry' : 'Add Contact Log Entry'}</DialogTitle>
                    <DialogDescription>
                        Notes are logged against this client's record and audited on save.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {(serverError || fieldError) && (
                        <Alert variant="danger" className="mb-4">
                            {fieldError ?? serverError}
                        </Alert>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        <FormField label="Date" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    min="1900-01-01"
                                    max={todayISO()}
                                    value={form.date}
                                    onChange={(e) => update('date', e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Contact type" required>
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.type}
                                    onChange={(e) => update('type', e.target.value as ContactLogType)}
                                >
                                    {CONTACT_LOG_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                    </div>

                    <FormField label="Contact by">
                        {(id) => (
                            <Input
                                id={id}
                                value={form.by}
                                onChange={(e) => update('by', e.target.value)}
                                placeholder="Defaults to your name"
                            />
                        )}
                    </FormField>

                    <FormField label="Notes / summary" required>
                        {(id) => (
                            <Textarea
                                id={id}
                                rows={4}
                                value={form.note}
                                onChange={(e) => update('note', e.target.value)}
                                placeholder="Summarize the contact and any follow-up actions…"
                            />
                        )}
                    </FormField>

                    <DialogFooter className="justify-between">
                        <div>
                            {isEdit && (
                                <Button
                                    type="button"
                                    variant={confirmingDelete ? 'danger' : 'ghost'}
                                    size="sm"
                                    onClick={onDelete}
                                    disabled={pending}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    {confirmingDelete ? 'Click again to confirm' : 'Delete'}
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={pending}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" disabled={pending}>
                                {pending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Entry'}
                            </Button>
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function initialForm(
    entry: ClientContactLogEntry | null,
    me: { first?: string; last?: string } | null | undefined,
): FormState {
    if (entry) {
        return {
            date: entry.date?.slice(0, 10) || todayISO(),
            type: entry.type,
            by: entry.by ?? '',
            note: entry.note ?? '',
        };
    }
    const meName = [me?.first, me?.last].filter(Boolean).join(' ');
    return {
        date: todayISO(),
        type: DEFAULT_TYPE,
        by: meName,
        note: '',
    };
}
